import { useAnalytics } from "../contexts/analytics/hooks";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { useErrorSnackbar } from "./useErrorSnackbar";

/**
 * Opens a file the backend hands over as a short-lived presigned URL.
 *
 * The tab has to be opened SYNCHRONOUSLY inside the click — a popup blocker rejects a `window.open`
 * that happens after an await — and only then pointed at the URL once the request resolves. That
 * ordering, plus `opener = null` (so the presigned URL can't reach back into the app) and closing the
 * blank tab on failure, is the whole reason this is shared rather than re-derived per caller: every
 * copy of it that drifts is a popup-blocked download or a dangling blank tab.
 *
 * Generic over WHICH file: the caller supplies the request and its telemetry, since the URL-minting
 * endpoints are support-gated per surface (a sync run's raw file, an SFTP delivery).
 */
export const useOpenPresignedFile = (): {
	openPresignedFile: (options: {
		/** Mints the URL. Any thrown error surfaces through the snackbar with `errorMessage`. */
		fetchUrl: () => Promise<{ data: { url: string } }>;
		/** Already-translated failure message — same contract as `showErrorSnackbar`. */
		errorMessage: string;
		analyticsEvent: string;
		/** Extra analytics properties; `department_id` is added for you. */
		analyticsProps?: Record<string, unknown>;
		/** `action` for the error report, e.g. "download sftp file". */
		action: string;
	}) => Promise<void>;
} => {
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const { track } = useAnalytics();

	const openPresignedFile: ReturnType<typeof useOpenPresignedFile>["openPresignedFile"] = async ({
		fetchUrl,
		errorMessage,
		analyticsEvent,
		analyticsProps,
		action,
	}) => {
		const tab = window.open("about:blank", "_blank");
		track(analyticsEvent, { department_id: chosenDepartment?.department_id, ...analyticsProps });
		try {
			const { data } = await fetchUrl();
			if (tab) {
				tab.opener = null;
				tab.location.href = data.url;
			} else {
				// Popup blocked despite the synchronous open — navigate the current tab rather than
				// silently doing nothing.
				window.location.href = data.url;
			}
		} catch (error) {
			tab?.close();
			showErrorSnackbar(errorMessage, { action }, error);
		}
	};

	return { openPresignedFile };
};
